/**
 * Today (docs/mobile-design.md §4.1-4.2): the phone's first tab. One request (/me/today) fills it, top to bottom:
 * the clock card, my shift, Needs me, approvals and who's in (people who approve) and unread chats. Since t182u the
 * phone opens on Home (the feed with a compact Today strip); this full screen is /today, one tap from the strip.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useQuery } from '@tanstack/react-query';
import { AtSign, CalendarDays, CheckSquare, ChevronLeft, ChevronRight, Coffee, GraduationCap, Inbox, LogIn, LogOut, MapPin, MapPinOff, MessageSquare, RotateCw, Stamp, Tablet, Users } from 'lucide-react';
import { CLOCK_ACTION_LABEL, CLOCK_ACTION_OP, PHONE_EVENT_LAYERS, WHOS_IN_ROWS, applyPendingClock, calItemHref, calTimeLabel, clockBlock, clockCard, evaluateGeofence, geoLabel, moreTraining, needsMeRows, pickClockLocation, todayCards, trainingDueLabel,
  type ClockAction, type ClockBlock, type GeoReading, type MeToday, type TodayChat } from '@tradeoutlet/shared';
import { useMeToday } from '@tradeoutlet/data';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { ApiError, api, errorMessage } from '@/lib/api';
import { clock as fmtClock, dt, formatDate, minutes } from '@/lib/format';
import { GEO_FAILURE_TEXT, readPosition, readPositionIfAllowed, type GeoFailure } from '@/lib/geo';
import { pendingClockIn, submitClock, usePendingClock } from '@/lib/clockOutbox';
import { WaitingMarker } from '@/components/clock/WaitingMarker';
import { Spinner } from '@/components/ui';
import { PendingHandoversModal } from '@/features/workforce/handovers/HandoversUi';
import { BottomSheet } from './BottomSheet';

const card = 'mx-3 rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900';
const rowCls = 'flex min-h-[48px] w-full items-center gap-3 px-4 text-left active:bg-slate-100 dark:active:bg-slate-800';
const EVENT_KEY = 'collab.clockin.event';

function Section({ title, icon, to, action, children, testId }: { title: string; icon: ReactNode; to?: string; action?: ReactNode; children: ReactNode; testId: string }) {
  return (
    <section className={card} data-testid={testId} aria-label={title}>
      <div className="flex min-h-[44px] items-center gap-2 pl-4 pr-1">
        <span className="text-slate-500" aria-hidden>{icon}</span>
        <h2 className="flex-1 text-sm font-semibold">{title}</h2>
        {action}
        {to && <Link to={to} className="flex h-11 min-w-[44px] items-center justify-center px-3 text-sm font-medium text-slate-600 dark:text-slate-300">All</Link>}
      </div>
      <div className="border-t border-slate-100 pb-1 dark:border-slate-800">{children}</div>
    </section>
  );
}
const Count = ({ n, tone = 'none' }: { n: number; tone?: 'none' | 'bad' }) =>
  <span className={clsx('text-xs font-semibold tabular-nums', tone === 'bad' ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200')}>{n}</span>;
function Row({ to, onClick, icon, children, end, testId }: { to?: string; onClick?: () => void; icon?: ReactNode; children: ReactNode; end?: ReactNode; testId?: string }) {
  const inner = <>{icon && <span className="shrink-0 text-slate-500" aria-hidden>{icon}</span>}<span className="min-w-0 flex-1">{children}</span>{end}<ChevronRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden /></>;
  return to ? <Link to={to} className={rowCls} data-testid={testId}>{inner}</Link> : <button type="button" onClick={onClick} className={rowCls} data-testid={testId}>{inner}</button>;
}

// ------------------------------------------------------------------------------------------------ clock card
/**
 * The clock flow shared by the Today clock card and the top bar (t184u): geofence reading, idempotent clock-in
 * (client_event_id kept in sessionStorage until it succeeds), the location-check sheet and the handovers prompt.
 * t187u: with no signal the punch is saved on the phone (lib/clockOutbox) with the tap time and the location fix and
 * shown straight away; `waiting` says the clock includes punches still to send.
 */
export function useClockFlow(t: MeToday) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const nav = useNavigate();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 15_000); return () => clearInterval(i); }, []);
  const pending = usePendingClock(ws);
  const c = useMemo(() => applyPendingClock(t.clock, pending), [t.clock, pending]);
  const waiting = c.pendingOps > 0;
  const state = clockCard(c, now);
  const [here, setHere] = useState<GeoReading | null>(null);
  const [block, setBlock] = useState<(ClockBlock & { failure?: string }) | null>(null);
  const [handovers, setHandovers] = useState(false);
  const [busy, setBusy] = useState(false);
  const fence = c.geofence;
  // Before clocking in: where you are now, only when the site already has location permission (no prompt).
  useEffect(() => { if (!fence || c.entry) return; let live = true; void readPositionIfAllowed().then(r => { if (live) setHere(r); }); return () => { live = false; }; }, [fence?.location_id, Boolean(c.entry)]); // eslint-disable-line react-hooks/exhaustive-deps
  const live = fence && here ? evaluateGeofence(fence, here, 'in') : null;
  const geo = c.entry ? geoLabel(c.entry.clock_in_geo, c.entry.clock_in_distance_m) : live ? geoLabel(live.geo, live.distance_m) : null;
  const shiftId = c.clock_shift_id ?? undefined;
  const locationId = shiftId ? undefined : (pickClockLocation(c.clock_locations, { home: c.home_location_id, position: here }) ?? undefined);
  const failureRef = useRef<{ text?: string; code?: GeoFailure }>({});
  const position = async (fresh = false) => {
    if (!fence) return null;
    const r = await readPosition(10_000, fresh);
    if (!r.reading && r.failure) failureRef.current = { text: GEO_FAILURE_TEXT[r.failure], code: r.failure };
    if (r.reading) setHere(r.reading);
    return r.reading;
  };
  const tap = async (action: ClockAction, retry = false) => {
    if (busy || !ws) return;
    setBlock(null);
    failureRef.current = {};
    setBusy(true);
    // The tap's own time, before waiting for a location fix: an offline punch is recorded at this moment.
    const device_time = new Date().toISOString();
    try {
      const kind = CLOCK_ACTION_OP[action];
      const client_event_id = action === 'in' ? sessionStorage.getItem(EVENT_KEY) ?? crypto.randomUUID() : crypto.randomUUID();
      if (action === 'in') sessionStorage.setItem(EVENT_KEY, client_event_id);
      const reading = action === 'in' || action === 'out' ? await position(retry) : null;
      const r = await submitClock(ws, action, {
        client_event_id, device_time, geo: reading, geo_failure: reading ? null : failureRef.current.code ?? null,
        ...(kind === 'clock.in' ? { shift_id: shiftId ?? null, location_id: locationId ?? null } : { clock_in_event_id: pendingClockIn(ws)?.payload.client_event_id ?? null }),
      });
      if (action === 'in') sessionStorage.removeItem(EVENT_KEY);
      if (r.queued) {
        // Saved on the phone. Say now what the manager will see (the same fence check the server makes later).
        const g = action === 'in' && fence ? evaluateGeofence(fence, reading, 'out').geo : null;
        toast(g === 'outside' ? `You're away from ${fence?.location_name ?? 'the building'}. Your manager will check this.` : g === 'unknown' ? 'Location not confirmed. Your manager will check this.' : 'Saved · sends when there\'s signal', 'info');
        return;
      }
      const e = r.entry;
      if (action === 'in') {
        if (e.clock_in_geo === 'outside' || e.clock_in_geo === 'unknown') toast(e.clock_in_geo === 'outside' ? 'You\'re away from the building - your manager will see this' : 'Location not confirmed - your manager will see this', 'info');
        toast('Clocked in', 'success');
        const pendingHandovers = await api.wf.pendingHandovers(ws).catch(() => []);
        if (pendingHandovers.length) setHandovers(true);
      } else if (action === 'out') toast('Clocked out - timesheet submitted', 'success');
      else toast(action === 'break' ? 'Break started' : 'Break ended', 'success');
    } catch (e) {
      const b = clockBlock(action, e instanceof ApiError ? { status: e.status, body: e.body } : null);
      if (b) setBlock({ ...b, failure: failureRef.current.text });
      else toast(errorMessage(e), 'error');
    } finally { setBusy(false); }
  };
  const where = c.entry ? null : c.location_name ?? c.clock_locations.find(l => l.id === locationId)?.name ?? null;
  const sheetsEl = (<>
      <BottomSheet open={Boolean(block)} onClose={() => setBlock(null)} title="Location check" testId="today-geo-sheet">
        <div className="px-4 pb-2">
          <p className="flex items-start gap-2 text-sm"><MapPinOff className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" aria-hidden />{block?.failure ?? block?.message}</p>
          {block?.distance_m != null && <p className="mt-1 pl-6 text-sm text-slate-500" data-testid="today-geo-sheet-distance">{geoLabel('outside', block.distance_m)?.text}</p>}
        </div>
        <button type="button" className={rowCls} onClick={() => void tap('in', true)} data-testid="today-geo-retry"><RotateCw className="h-5 w-5 text-slate-500" aria-hidden />Try again</button>
        <button type="button" className={rowCls} onClick={() => { setBlock(null); nav('/settings/account/kiosk'); }} data-testid="today-geo-kiosk"><Tablet className="h-5 w-5 text-slate-500" aria-hidden />Use the kiosk</button>
      </BottomSheet>
      <PendingHandoversModal open={handovers} onClose={() => setHandovers(false)} />
  </>);
  return { state, clock: c, geo, where, tap, busy, waiting, sheetsEl };
}

/** t187u: a punch saved on the phone and not sent yet. */
export { WaitingMarker };

export function ClockCard({ t }: { t: MeToday }) {
  const { state, geo, where, tap, busy, waiting, sheetsEl } = useClockFlow(t);
  const Icon = { in: LogIn, out: LogOut, break: Coffee, resume: Coffee }[state.primary];
  return (
    <section className={clsx(card, 'p-4')} data-testid="today-clock" data-state={state.state} aria-label="Time clock">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {state.state === 'out'
            ? <p className="text-base font-semibold">Not clocked in</p>
            : <p className="text-2xl font-semibold tabular-nums" data-testid="today-worked">{minutes(state.worked_minutes)}</p>}
          <p className="truncate text-sm text-slate-500">
            {state.state === 'break' ? 'On break' : state.since ? `Since ${fmtClock(dt(state.since))}` : where ?? ''}
          </p>
          {waiting && <WaitingMarker className="mt-0.5" />}
        </div>
        {geo && (
          <span data-testid="today-geo" className={clsx('flex shrink-0 items-center gap-1 text-xs font-medium', geo.tone === 'ok' ? 'text-emerald-800 dark:text-emerald-200' : 'text-orange-800 dark:text-orange-200')}>
            {geo.tone === 'ok' ? <MapPin className="h-3.5 w-3.5" aria-hidden /> : <MapPinOff className="h-3.5 w-3.5" aria-hidden />}{geo.text}
          </span>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={() => void tap(state.primary)} disabled={busy} data-testid="today-clock-primary"
          className={clsx('flex h-12 flex-1 items-center justify-center gap-2 rounded-xl text-base font-semibold disabled:opacity-60',
            state.primary === 'in' ? 'bg-accent text-on-accent ring-1 ring-inset ring-black/10' : state.primary === 'out' ? 'bg-ink text-white dark:bg-white dark:text-ink' : 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white')}>
          {busy ? <Spinner className="h-5 w-5" /> : <Icon className="h-5 w-5" aria-hidden />}{CLOCK_ACTION_LABEL[state.primary]}
        </button>
        {state.secondary && (
          <button type="button" onClick={() => void tap(state.secondary!)} disabled={busy} data-testid="today-clock-secondary"
            className="flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 text-base font-medium disabled:opacity-60 dark:border-slate-700">
            {state.secondary === 'break' ? <Coffee className="h-5 w-5" aria-hidden /> : <LogOut className="h-5 w-5" aria-hidden />}{state.secondary === 'break' ? 'Break' : 'Clock out'}
          </button>
        )}
      </div>
      {sheetsEl}
    </section>
  );
}

// ------------------------------------------------------------------------------------------------ the rest
const dueCls = (overdue: boolean) => clsx('shrink-0 text-xs font-medium', overdue ? 'text-rose-700 dark:text-rose-400' : 'text-slate-500');
/** t361u: tasks due and outstanding training share one list by due date (overdue first); each training row opens the course. */
function NeedsMe({ n, today }: { n: MeToday['needs_me']; today: string }) {
  const openTask = useUi(s => s.openTask);
  const more = moreTraining(n);
  return (
    <Section title="Needs me" icon={<Inbox className="h-4 w-4" />} testId="today-needs">
      {needsMeRows(n).map(r => {
        if (r.kind === 'task') {
          const t = r.task;
          return (
            <Row key={`t${t.id}`} onClick={() => openTask(t.id)} icon={<CheckSquare className="h-4 w-4" />} testId="today-task"
              end={<span className={dueCls(t.overdue)}>{t.overdue ? `Overdue · ${formatDate(t.due_date)}` : 'Today'}</span>}>
              <span className="block truncate text-sm">{t.title}</span>
            </Row>
          );
        }
        const due = trainingDueLabel(r.training.due_on, today);
        return (
          <Row key={`c${r.id}`} to={`/training/${r.training.course_id}`} icon={<GraduationCap className="h-4 w-4" />} testId="today-training"
            end={due && <span className={dueCls(due.overdue)} data-testid="today-training-due">{due.text}</span>}>
            <span className="block truncate text-sm">{r.training.title}</span>
          </Row>
        );
      })}
      {n.tasks_total > n.tasks.length && <Row to="/tasks" end={<Count n={n.tasks_total - n.tasks.length} />}><span className="text-sm text-slate-600 dark:text-slate-300">More tasks</span></Row>}
      {more > 0 && <Row to="/training" end={<Count n={more} />} testId="today-training-more"><span className="text-sm text-slate-600 dark:text-slate-300">More training</span></Row>}
      {n.acks > 0 && <Row to="/acknowledgements" icon={<Stamp className="h-4 w-4" />} end={<Count n={n.acks} tone="bad" />} testId="today-acks"><span className="text-sm">To acknowledge</span></Row>}
      {n.mentions.map(c => <ChatRow key={c.id} c={c} mention />)}
    </Section>
  );
}
function ChatRow({ c, mention }: { c: TodayChat; mention?: boolean }) {
  return (
    <Row to={`/c/${c.id}`} icon={mention ? <AtSign className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />} testId={mention ? 'today-mention' : 'today-chat'}
      end={<Count n={mention ? c.mention_count : c.unread_count} tone={mention ? 'bad' : 'none'} />}>
      <span className="block truncate text-sm font-medium">{c.name}</span>
    </Row>
  );
}
function Approvals({ a }: { a: NonNullable<MeToday['manager']>['approvals'] }) {
  const rows = [
    { n: a.timesheets, label: 'Timesheets', to: '/rota/approvals?kind=timesheets' },
    { n: a.leave, label: 'Time off', to: '/rota/approvals?kind=leave' },
    { n: a.swaps, label: 'Shift swaps', to: '/rota/approvals?kind=swaps' },
    { n: a.drops ?? 0, label: 'Shift drops', to: '/rota/approvals?kind=drops' },
    { n: a.claims, label: 'Open shift claims', to: '/rota/approvals?kind=claims' },
  ].filter(r => r.n > 0);
  return (
    <Section title="Approvals" icon={<CheckSquare className="h-4 w-4" />} testId="today-approvals">
      {rows.map(r => <Row key={r.label} to={r.to} end={<Count n={r.n} tone="bad" />}><span className="text-sm">{r.label}</span></Row>)}
    </Section>
  );
}
function WhosIn({ w }: { w: NonNullable<NonNullable<MeToday['manager']>['whos_in']> }) {
  const rows = WHOS_IN_ROWS.filter(r => (w[r.id] ?? 0) > 0);
  const tone = { ok: 'text-emerald-700 dark:text-emerald-400', warn: 'text-orange-700 dark:text-orange-400', bad: 'text-rose-700 dark:text-rose-400', none: 'text-slate-900 dark:text-white' };
  return (
    <Section title="Who's in" icon={<Users className="h-4 w-4" />} to="/workforce/timesheets?tab=team" testId="today-whos-in">
      <Link to="/workforce/timesheets?tab=team" className="grid grid-cols-3 gap-y-2 px-4 py-3">
        {rows.map(r => (
          <span key={r.id} className="flex flex-col" data-testid={`whos-in-${r.id}`}>
            <span className={clsx('text-xl font-semibold tabular-nums', tone[r.tone])}>{w[r.id]}</span>
            <span className="text-xs text-slate-500">{r.label}</span>
          </span>
        ))}
      </Link>
    </Section>
  );
}

/** t213u: today's events, linking to the phone calendar. */
function TodayCalendar() {
  const ws = useWorkspaceId();
  const day = DateTime.now().setZone('Europe/London').toISODate()!;
  const next = DateTime.fromISO(day).plus({ days: 1 }).toISODate()!;
  const q = useQuery({ queryKey: ['calendar', ws ?? '', 'phone-today', day], queryFn: () => api.calendar(ws!, { from: day, to: next, include_attending: '1', layers: PHONE_EVENT_LAYERS }), enabled: Boolean(ws), staleTime: 60_000 });
  const items = (q.data?.items ?? []).filter(i => i.attending || PHONE_EVENT_LAYERS.includes(i.layer)).sort((a, b) => Number(b.all_day) - Number(a.all_day) || a.start.localeCompare(b.start));
  return (
    <Section title="Calendar" icon={<CalendarDays className="h-4 w-4" />} to="/calendar" testId="today-calendar">
      {items.length === 0 ? <Row to="/calendar"><span className="text-sm text-slate-600 dark:text-slate-300">Nothing today</span></Row>
        : items.slice(0, 3).map(it => (
          <Row key={it.id} to={calItemHref(it) ?? '/calendar'} testId="today-event" end={<span className="text-xs tabular-nums text-slate-500">{calTimeLabel(it)}</span>}>
            <span className="block truncate text-sm">{it.title}</span>
          </Row>
        ))}
    </Section>
  );
}

export default function TodayPage() {
  const ws = useWorkspaceId();
  const nav = useNavigate();
  const q = useMeToday(ws);
  const t = q.data;
  if (!t) return <div className="flex h-full items-center justify-center">{q.error ? <p className="text-sm text-rose-700">{errorMessage(q.error)}</p> : <Spinner />}</div>;
  // t229u: the top bar already has the clock and the current / next shift, so this view starts with the actions.
  const cards = todayCards(t).filter(k => k !== 'clock' && k !== 'shift');
  return (
    <div className="h-full overflow-y-auto bg-slate-50 pb-3 dark:bg-slate-950" data-testid="today-page">
      <div className="flex items-center border-b border-slate-200 bg-white pr-2 dark:border-slate-800 dark:bg-slate-900">
        <button type="button" onClick={() => nav(-1)} aria-label="Back" className="flex h-12 w-11 items-center justify-center text-slate-700 dark:text-slate-200"><ChevronLeft className="h-6 w-6" /></button>
        <h1 className="text-base font-semibold">All actions</h1>
      </div>
      <div className="space-y-3 pt-3">
      {cards.map(k => {
        switch (k) {
          case 'needs_me': return <NeedsMe key={k} n={t.needs_me} today={t.date} />;
          case 'approvals': return <Approvals key={k} a={t.manager!.approvals} />;
          case 'whos_in': return <WhosIn key={k} w={t.manager!.whos_in!} />;
          case 'chats': return (
            <Section key={k} title="Unread chats" icon={<MessageSquare className="h-4 w-4" />} to="/chat" testId="today-chats">
              {t.unread_chats.map(c => <ChatRow key={c.id} c={c} />)}
            </Section>
          );
          case 'feed': return null; // the feed is Home itself (t182u)
        }
      })}
      <TodayCalendar />
      </div>
    </div>
  );
}
